import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from '../analytics';

const metrics = [
  { value: "7+ Years", label: "Product Experience" },
  { value: "Products of all stages", label: "Shipped from 0→1" },
  { value: "10.48x", label: "Activation Multiplier" },
  { value: "20% MRR increase", label: "ARR Unlocked" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.32, 0.72, 0, 1] },
  },
};

function Hero() {
  const [isExpanded, setIsExpanded] = useState(false);

  const firstSentence = "With 7+ years in product management, I specialize in data-driven platform and marketplace products that scale from ideation to revenue.";
  
  const fullText = (
    <>
      <p className="first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:text-5xl first-letter:font-bold first-letter:text-primary-500 first-letter:dark:text-primary-400 first-letter:sm:text-6xl first-letter:leading-none">
        With 7+ years in product management, I specialize in data-driven
        platform and marketplace products that scale from ideation to
        revenue. My career spans global, remote-first organizations—from
        founding efitter (B2C→B2B pivot) to shipping monetization
        infrastructure at Meta and leading data management strategy at
        Toggl.
      </p>
      <p>
        As a former founder, I bring a customer-first mindset balanced with
        commercial acumen. I've led products that drove{" "}
        <span className="font-semibold text-primary-500 dark:text-primary-300">33%</span> retention
        increases, unlocked{" "}
        <span className="font-semibold text-primary-500 dark:text-primary-300">$700K</span> in
        ARR, and achieved{" "}
        <span className="font-semibold text-primary-500 dark:text-primary-300">10.48x</span>{" "}
        activation multipliers. I thrive in complex, cross-functional problem
        spaces—from authentication systems to analytics infrastructure—and
        excel at translating ambiguous requirements into shipped features.
      </p>
      <p>
        I'm AI-native in my workflow, using tools like Claude and Cursor to
        accelerate research, prototyping, and delivery. This dashboard itself
        is an example: rapidly prototyped to demonstrate not just what I've
        built, but how I build.
      </p>
    </>
  );

  return (
    <section
      id="overview"
      className="bg-white dark:bg-gray-900 px-6 py-12 sm:px-8 sm:py-14 md:px-12 md:py-16 lg:px-16 lg:py-20 transition-colors duration-200"
    >
      <motion.div
        className="mx-auto max-w-4xl"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          variants={itemVariants}
          className="mb-4 text-3xl font-bold leading-tight tracking-tight text-primary-500 dark:text-primary-400 sm:text-4xl md:text-5xl"
        >
          Building data-driven products that scale from ideation to revenue generation
        </motion.h1>
        <motion.p
          variants={itemVariants}
          className="mb-6 text-lg text-gray-600 dark:text-gray-300 sm:mb-8 md:text-xl"
        >
          Product Leader | Data & Platform Products | AI-Native Builder
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="mb-14 max-w-[700px] space-y-6 overflow-hidden leading-[1.75] text-gray-900 dark:text-gray-100 sm:mb-16 sm:leading-[1.8] md:mb-20"
        >
          <div id="hero-text-content">
            <AnimatePresence mode="wait">
              {!isExpanded ? (
                <motion.div
                  key="collapsed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <p className="first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:text-5xl first-letter:font-bold first-letter:text-primary-500 first-letter:dark:text-primary-400 first-letter:sm:text-6xl first-letter:leading-none">
                    {firstSentence}
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="expanded"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                  className="space-y-6"
                >
                  {fullText}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <button
            type="button"
            onClick={() => {
              const newExpanded = !isExpanded;
              setIsExpanded(newExpanded);
              trackEvent('hero_text_expanded', {
                expanded: newExpanded,
              });
            }}
            className="mt-4 text-sm font-semibold text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 rounded px-2 py-1"
            aria-expanded={isExpanded}
            aria-controls="hero-text-content"
          >
            {isExpanded ? "Read less" : "Read more"}
          </button>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="clear-both grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:gap-6"
        >
          {metrics.map((metric) => (
            <motion.div
              key={metric.label}
              variants={itemVariants}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-5 transition-all duration-200 hover:-translate-y-1 hover:border-primary-500 dark:hover:border-primary-400 hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-primary-500/10 sm:px-7 sm:py-6"
            >
              <p className="mb-1 text-2xl font-bold text-primary-500 dark:text-primary-400 sm:text-3xl md:text-3xl">
                {metric.value}
              </p>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300 sm:text-base">
                {metric.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

export default Hero;
